import { useLocation, useNavigate, useParams } from "react-router";
import { CompaniesService } from "./CompaniesService";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Company, useUserNew } from "src/api/client-api/useUserNew";
import { companySlug, ssqSlug, URLHandler } from "../URLHandler";
import { switchUrl, whereAmI } from "../urlSwitcher";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { getSelectableCompanies } from "src/context/permissionMatching";

export const useSwitchCompany = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isFeatureActivated } = useApplicationParameters();
    const { selectedCompany, setSelectedCompany } = useFoodpilotContext();
    const { user } = useUserNew();
    const params = useParams();

    const url = location.pathname;

    const goToHomeFallback = () => {
        if (isFeatureActivated("FS-cms-enabled")) {
            return URLHandler.home();
        }
        return null;
    };

    const _switchUrl = (companyId: number) => {
        const selectableCompanies = user === undefined ? [] : getSelectableCompanies(user);
        const newSelectedCompany = CompaniesService.selectCurrentCompany(selectableCompanies, companyId);
        if (newSelectedCompany === null) {
            const homefallback = goToHomeFallback();
            if (homefallback === null) {
                // logout ? do nothing today
                return;
            }
            return navigate(homefallback);
        }

        const newUrl = getNewUrl(newSelectedCompany);
        if (newUrl === null) {
            // Somehow, we do not know where to go. Do nothing.
            return;
        }

        CompaniesService.switchCompany(newSelectedCompany.id);
        setSelectedCompany(newSelectedCompany);
        navigate(newUrl);
    };

    const getNewUrl = (company: Company): string | null => {
        const companyId = company.id;

        const myLocation = whereAmI({ url });

        if (myLocation.inProduct) {
            const productsUrl = URLHandler.product.list(companyId);
            return productsUrl;
        }

        if (myLocation.inIngredients) {
            const ingredientsUrl = URLHandler.ingredient.list(companyId);
            return ingredientsUrl;
        }

        if (myLocation.inSuppliers) {
            const suppliersUrl = URLHandler.supplier.list(companyId);
            return suppliersUrl;
        }

        if (myLocation.inPackagings) {
            const packagingsUrl = URLHandler.packaging.list(companyId);
            return packagingsUrl;
        }

        if (myLocation.inCompany) {
            const foundSameSsq = company?.organizationSector?.ssqs?.find((v) => v.ssqId === Number(params.ssqId));
            if (foundSameSsq === undefined) {
                // Can't move to same QSS ? Go home;
                return URLHandler.company.overview(companyId);
            } else {
                // Same ssq means : Another company, but same ssq + same campaign + same score + same heading + ...
                const newSsqId = foundSameSsq.ssqId;
                const newUrl = switchUrl({
                    url,
                    options: [
                        { slug: companySlug, newId: companyId },
                        { slug: ssqSlug, newId: newSsqId },

                        // If we're on a page with `overview`, the ID is the ssqId, and we want to change it aswell.
                        { slug: "overview", newId: newSsqId },
                    ],
                });
                return newUrl;
            }
        }
        const newUrl = switchUrl({
            url,
            options: [{ slug: companySlug, newId: companyId }],
        });

        return newUrl;
    };

    if (selectedCompany === null) {
        CompaniesService.resetCompany();
    }

    return {
        changeCompany: _switchUrl,
    };
};
